"use client";

import React, { useMemo } from "react";
import Image from "next/image";
import _style from "../styles.parts";
import Consts from "@common/config/consts";
import { dayjs, logjs, utils } from "@lib/client";

import { usePartsList } from "../usePartsList";

const log = new logjs("PartsEditListArea");

// 初期値の定義
const INIT_PARTS_DATA = {
  PartsJyunseiCode: "",
  PartsKindID: -1,
  StanderdPrice: 0,
  Soryo: "",
  Hasso: "",
  Sell_Limit: "",
  ColorNo: "",
  ColorName: "",
  Bikou: "",
  JishaNo: "",
  PrepKind: "",
  HoshoSelect: "",
};

// スタイル定数
const INPUT_STYLE = { width: "8em" };
const ICON_CONTAINER_STYLE = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
};

/**
 * 他社成約情報を取得する
 * 部品IDに基づいて他社成約の表示有無、列数、スタイル、成約金額を判定する
 * @param {number} pl_prid - 部品ID（PRID）
 * @param {Object} data - データオブジェクト（prSeiyaku、prMySeiyakuを含む）
 * @returns {Object} 他社成約情報
 *   - thCol: テーブルヘッダーの列数（12または14）
 *   - thColor: テーブルヘッダーのスタイル
 *   - showOtherCompanyContract: 他社成約を表示するかどうか
 *   - seiyakuPrice: 成約金額（価格+送料）
 */
function getOtherCompanyContractInfo(pl_prid, data) {
  let thCol = 14;
  let thColor = {};
  let showOtherCompanyContract = false;
  let seiyakuPrice = null;

  // 通常部品（PRID > 0）の場合のみ他社成約情報をチェック
  if (pl_prid > 0) {
    const hacchuflg = data?.prMySeiyaku?.[pl_prid];

    // 他社成約データが存在する場合
    if (data?.prSeiyaku?.[pl_prid]) {
      // 発注フラグが2, 5, 8, 9以外の場合のみ他社成約を表示
      // （これらのフラグは特定の状態を示し、他社成約表示を抑制する）
      if (hacchuflg !== 2 && hacchuflg !== 5 && hacchuflg !== 8 && hacchuflg !== 9) {
        thCol = 12; // 他社成約表示時は列数を12に変更
        thColor = { borderLeftStyle: "none", background: "#696969", color: "#FFF" };
        showOtherCompanyContract = true;

        // 成約金額を計算（価格 + 送料）
        if (hacchuflg !== undefined && hacchuflg !== null) {
          const seiyakuData = data?.prSeiyaku?.[pl_prid];
          if (seiyakuData?.Price !== undefined && seiyakuData?.Soryo !== undefined) {
            seiyakuPrice = (Number(seiyakuData.Price) || 0) + (Number(seiyakuData.Soryo) || 0);
          }
        }
      }
    }
  }

  return { thCol, thColor, showOtherCompanyContract, seiyakuPrice };
}

/**
 * 部品名とカテゴリアイコンを生成する
 * その他部品（PRID=0）の場合は他社部品名を、通常部品の場合は純正部品名を使用
 * カテゴリアイコンはPKID1-5から生成し、PKID3（外装）は別途処理
 * @param {Object} firstPartsItem - 最初の部品アイテム
 * @param {number} pl_prid - 部品ID（PRID、0の場合はその他部品）
 * @param {Array} partskind - 部品種別リスト
 * @param {Object} formProps - フォームプロパティ（getCategoryName、getCategoryStyleを含む）
 * @returns {Object} 部品名、カテゴリアイコン、PKID3フラグ
 *   - partsName: 部品名文字列
 *   - categoryIcons: カテゴリアイコンのJSX要素配列
 *   - pk3flg: PKID3（外装）が存在する場合は1、それ以外は0
 */
function generatePartsNameAndIcons(firstPartsItem, pl_prid, partskind, formProps) {
  log.debug("generatePartsNameAndIcons ", { firstPartsItem, pl_prid, partskind });
  let partsName = "";
  let categoryIcons = [];
  let pk3flg = 0;

  // その他部品（PRID=0）の場合
  if (pl_prid === 0) {
    partsName = firstPartsItem?.OthPartsName || "";
    // その他部品の詳細品名がある場合は追加
    if (firstPartsItem?.OthPartsSonota) {
      partsName += `　品名：${firstPartsItem.OthPartsSonota}`;
    }
  } else {
    // 通常部品の場合
    partsName = firstPartsItem?.P_PartsName || "";

    // PKID1-5からカテゴリアイコンを生成
    // PKID3（外装）は最後に別途表示するため、ここではスキップ
    const pkids = [firstPartsItem?.P_PKID1, firstPartsItem?.P_PKID2, firstPartsItem?.P_PKID3, firstPartsItem?.P_PKID4, firstPartsItem?.P_PKID5];

    pkids.forEach((pkid, idx) => {
      // PKIDが存在しない、またはPKID3（外装）の場合はスキップ
      if (!pkid || pkid === 3) {
        if (pkid === 3) pk3flg = 1; // PKID3フラグを設定
        return;
      }

      // カテゴリ名とスタイルを取得してアイコンを生成
      const categoryName = formProps.getCategoryName(pkid, partskind);
      const categoryStyle = formProps.getCategoryStyle(pkid);
      categoryIcons.push(
        <span key={idx} style={categoryStyle}>
          {categoryName}
        </span>
      );
    });
  }

  return { partsName, categoryIcons, pk3flg };
}

/**
 * 保証選択のオプションを生成する
 * 保証選択データのオブジェクトをselect要素用のオプション配列に変換する
 * @param {Object} hoshoSelect - 保証選択データ（キー: 値のオブジェクト）
 * @returns {Array} オプション配列
 *   各オプションは { key: string, value: string, label: string } の形式
 */
function generateHoshoSelectOptions(hoshoSelect) {
  log.debug("generateHoshoSelectOptions ", { hoshoSelect });
  const options = [];
  Object.keys(hoshoSelect).forEach((hs) => {
    options.push({ key: hs, value: hs, label: hoshoSelect[hs] });
  });
  return options;
}

/**
 * 写真アイコンをレンダリングする
 * 写真とファイルアップロードのアイコンを表示し、クリック時にダイアログを開く
 * @param {Object} params - パラメータ
 *   - is_readOnly: 読み取り専用モードかどうか
 *   - pl_prid: 部品ID（PRID、0の場合はその他部品）
 *   - paid: 部品回答ID
 *   - partsItem: 部品アイテム
 *   - seqnum: シーケンス番号
 *   - data: データオブジェクト（resPict、resFileを含む）
 *   - resPict: 写真リソースデータ
 *   - quote_number: 見積番号
 *   - photoDialogProps: 写真ダイアログのプロパティ
 *   - fileupDialogProps: ファイルアップロードダイアログのプロパティ
 *   - formProps: フォームプロパティ（handlePhotoClick、handleFileupClickを含む）
 * @returns {JSX.Element} 写真アイコンコンポーネント
 */
function renderPhotoIcons({ is_readOnly, pl_prid, paid, partsItem, seqnum, data, resPict, quote_number, photoDialogProps, fileupDialogProps, formProps }) {
  // 写真アイコンの画像名を決定（写真がある場合はcamera_icon2.png）
  const hasPict = partsItem?.isPhoto || false;
  const hasFile = data?.resFile?.[paid] || false;

  const iconName = hasPict ? "camera_icon2.png" : "camera_icon.png";

  // 参照モードを決定（読み取り専用の場合は参照モード、編集可能な場合は書き込みモード）
  const wreckerPhoto = is_readOnly ? Consts.WRECKER_PHOTO.KARI_REF : Consts.WRECKER_PHOTO.KARI_WRT;

  /**
   * 写真とファイルアイコンをレンダリングする内部関数
   * @param {number} partsId - 部品ID（その他部品の場合は0）
   * @param {number} seqnumValue - シーケンス番号
   * @returns {JSX.Element} アイコンコンテナ
   */
  const renderIcons = (partsId, seqnumValue) => (
    <div style={ICON_CONTAINER_STYLE}>
      {/* 写真アイコン（読み取り専用でない、または写真がある場合に表示） */}
      {(!is_readOnly || hasPict) && (
        <a onClick={formProps.handlePhotoClick(photoDialogProps, quote_number, partsId, seqnum, wreckerPhoto)} style={{ cursor: "pointer" }}>
          <Image src={is_readOnly ? "/images/camera_icon.png" : `/images/${iconName}`} alt="写真" width={24} height={20} />
        </a>
      )}
      {/* ファイルアイコン（読み取り専用でない、またはファイルがある場合に表示） */}
      {(!is_readOnly || hasFile) && (
        <a onClick={formProps.handleFileupClick(fileupDialogProps, quote_number, partsId, seqnum, wreckerPhoto)} style={{ cursor: "pointer" }}>
          <Image src="/images/file_icon.png" alt="ファイル" width={20} height={16} />
        </a>
      )}
    </div>
  );

  // その他部品（PRID=0）の場合は部品IDを0、シーケンス番号を調整
  if (pl_prid === 0) {
    return renderIcons(0, is_readOnly ? seqnum : 1);
  } else {
    // 通常部品の場合は部品IDとシーケンス番号を使用
    return renderIcons(partsItem?.PRID, seqnum);
  }
}

/**
 * 部品情報編集表示コンポーネント
 * partseditlist.ctpをReactに移植した編集フォームコンポーネント
 * 既存の回答データを編集・表示し、Hacchu_flgによる入力/ラベル表示の切り替え、
 * 在庫確認ボタン、他社成約表示などの複雑なロジックを含む
 *
 * @param {Object} props - コンポーネントのプロパティ
 *   - data: 部品データ（partsListReq、partsListAns、answer、request等を含む）
 *   - state: 状態オブジェクト（initData等を含む）
 *   - actions: アクション関数
 *   - quote_number: 見積番号
 *   - partsData: 部品データの状態
 *   - setPartsData: 部品データの状態更新関数
 *   - photoDialogProps: 写真ダイアログのプロパティ
 *   - fileupDialogProps: ファイルアップロードダイアログのプロパティ
 *   - onAddRow: 行追加時のコールバック（未使用）
 *   - qaDialogProps: 質問ダイアログのプロパティ
 * @returns {JSX.Element} 部品情報編集フォーム
 */
export default function PartsEditListArea({ data, state, actions, quote_number, partsData, setPartsData, photoDialogProps, fileupDialogProps, onAddRow, qaDialogProps, qaabDialogProps }) {
  log.debug("START ");

  log.debug("---------------------  PartsEditListArea data", data);

  // フォームの初期状態を設定
  const initState = {
    mitsumoriNo: quote_number,
    request: data?.request,
    answer: data?.answer,
    AqbData: data?.AqbData ?? {},
  };

  // 部品リスト用のカスタムフックを使用
  const [form, formProps] = usePartsList(initState, state, actions);

  // 依頼期限がCARRY_TAXIN_DATE以降かどうかを判定
  // これにより送料の表示方法（税別/税込）が決まる
  // PHPテンプレート: strtotime($request['OrderedRequest']['Request_Limit']) >= strtotime(Consts::CARRY_TAXIN_DATE)
  const isAfterCarryTaxinDate = formProps.isAfterCarryTaxinDate(data?.request?.Request_Limit);

  // 質問ボタンの表示判定
  // 依頼期限が現在日時以降（または同日）の場合のみ表示
  // PHPテンプレート: strtotime($request['Request_Limit']) >= strtotime(date('Y-m-d H:i:s'))
  const showQaButton = data?.request?.Request_Limit && (dayjs(data.request.Request_Limit).isAfter(dayjs()) || dayjs(data.request.Request_Limit).isSame(dayjs(), "day"));

  // 現車と紐付いている場合は色を変更するためのクラス名を設定
  // PHPテンプレート: if(!empty($answer[0]['RequestAnswer']['GenshaID'])){ $tr_class = 'gensha'; }
  const isGensha = !!data?.answer?.[0]?.GenshaID;
  const trClass = isGensha ? "gensha" : "";

  // 最終回答者の表示
  // PHPテンプレート: $answer[0]['CA']['UserName']
  const finalAnswerer = data?.answer?.[0]?.CA?.UserName;

  /**
   * 部品データを更新する
   * @param {string|number} paid - 部品回答ID
   * @param {number} index - 行インデックス
   * @param {string} target_key - 更新対象のキー
   * @param {any} value - 更新値
   */
  const addPartsData = (paid, index, target_key, value) => {
    log.debug(`---------- addPartsData paid:${paid} index:${index} target_key:${target_key} value:${value}`);

    setPartsData((prev) => {
      const currentRow = prev[paid] || [];
      const updatedRow = [...currentRow];

      // indexが存在しない場合は初期化
      if (!updatedRow[index]) {
        updatedRow[index] = {};
      }

      // 新しいオブジェクトを作成して更新
      updatedRow[index] = {
        ...updatedRow[index],
        [target_key]: value,
      };

      return {
        ...prev,
        [paid]: updatedRow,
      };
    });
  };

  // 部品依頼リストをクローンし、その他部品（PRID=0）を追加
  // その他部品はpartsListAnsから取得してmergePartsListに追加する
  const mergePartsList = useMemo(() => {
    const partsListReq = data?.partsListReq || [];
    const partsListAns = data?.partsListAns || [];
    const merged = utils.deepClone(partsListReq);
    for (const partsItem of partsListAns) {
      // 通常部品（PRID != 0）はスキップ（既にpartsListReqに含まれている）
      if (partsItem.PRID != 0) {
        continue;
      }
      // その他部品の場合は部品名を設定し、フラグを立てて追加（不変性を保つため新しいオブジェクトを作成）
      merged.push({
        ...partsItem,
        P_PartsName: partsItem.OthPartsName,
        is_add_parts: true,
      });
    }
    return merged;
  }, [data?.partsListReq, data?.partsListAns]);

  // データが存在しない、または部品依頼リストが空の場合は何も表示しない
  if (!data || !data.partsListReq || data.partsListReq.length === 0) {
    log.debug("データが存在しない、または部品依頼リストが空の場合は何も表示しない");
    return null;
  }

  // その他のデータの初期化（デフォルト値を設定）
  const partskind = state?.initData?.partskind || [];
  const answer = data?.answer?.[0] || {};
  const hoshoSelect = data?.hoshoSelect || [];
  const prSeiyaku = data?.prSeiyaku || {};
  const prMySeiyaku = data?.prMySeiyaku || {};
  const resPict = data?.resPict || {};
  const resFile = data?.resFile || {};
  const Request_Limit = data?.request?.Request_Limit;

  log.debug("data", data);
  log.debug("partsData", partsData);
  log.debug("mergePartsList", mergePartsList);

  return (
    <>
      {/* ヘッダーセクション */}
      <_style.PartsTitle>
        部品情報
        {/* 質問ボタン（Request_Limit >= 現在日時の場合のみ表示）@@TODO 要チェック */}
        {showQaButton && data?.partsListReq?.[0]?.id && (
          <_style.QuestionButton
            data-key={data?.partsListReq?.[0]?.id}
            onClick={(e) => {
              e.preventDefault();
              qaDialogProps.open({
                MitsumoriNo: quote_number,
                isQuestion: state.user?.Gyoshu_flg === 1,
                request: data?.request,
                answer: data?.answer,
              });
            }}
          >
            依頼元へ質問を行う | 回答を見る
          </_style.QuestionButton>
        )}
        {/* 入庫済み車輌ボタン　@@TODO 要チェック  */}
        {showQaButton && isGensha && (
          <a
            style={{ verticalAlign: "middle", marginTop: "-5px" }}
            className="important-btn4"
            onClick={(e) => {
              e.preventDefault();
              const genshaId = data?.answer?.[0]?.GenshaID;
              formProps.openGenshaDetail(genshaId);
            }}
          >
            <Image src="/images/gensha_icon_w.png" alt="入庫済み車輌" width={20} height={20} />
            入庫済みの車輌があります
          </a>
        )}
      </_style.PartsTitle>
      {/* 送料メッセージ（PHPテンプレート202-203行目相当） */}
      {isAfterCarryTaxinDate && (
        <div style={{ fontSize: "12pt", color: "#0000ff" }}>
          <strong>※送料は「税別」にて記載してください。</strong>
        </div>
      )}
      {/* 最終回答者の表示 */}
      {finalAnswerer && (
        <_style.FinalAnswererContainer>
          <_style.FinalAnswererLabel>最終回答者： {finalAnswerer} 様</_style.FinalAnswererLabel>
        </_style.FinalAnswererContainer>
      )}
      {/* 部品リストテーブル */}
      {/* PHPテンプレート: <table class="normal order_info"> */}
      {/* PHPテンプレート: $s = 0; for ($i = 0; $i < count($partsList); $i++): */}
      <_style.PLTable className="normal order_info">
        {mergePartsList.map((partsItem, partsIndex) => {
          log.debug(`--------------------------------- partsIndex:${partsIndex} partsItem`, partsItem);
          let { id, is_add_parts, OthParts4CD, P_PartsName } = partsItem;
          log.debug(`first is_add_parts:${is_add_parts} `);
          is_add_parts = utils.isNull(is_add_parts) ? false : is_add_parts;

          // その他部品の場合はOthParts4CD、通常部品の場合はidをキーとして使用
          const target_key = is_add_parts ? "add_parts_id-" + OthParts4CD : id;

          // partsDataから該当する回答データを取得
          const partsAnsItemRaw = partsData[target_key] || [];

          log.debug(`target_key:${target_key} is_add_parts:${is_add_parts} partsAnsItemRaw:`, partsAnsItemRaw);

          const pl_prid = partsItem.PRID || partsItem.id;
          const pl_paid = partsItem.id;
          //const firstPartsItem = partsAnsItemRaw[0] || partsItem;
          const firstPartsItem = partsItem;

          // partsDataから該当する回答データを取得（状態管理されている場合はそれを使用）
          // partsData[target_key]が配列で、かつ要素がある場合のみ使用
          const partsAnsItem = Array.isArray(partsAnsItemRaw) && partsAnsItemRaw.length > 0 ? partsAnsItemRaw : [];

          log.debug(`partsIndex:${partsIndex}--------------------------------------------`);
          log.debug(`target_key:${target_key}`, partsData);
          log.debug("partsAnsItemRaw", partsAnsItemRaw);
          log.debug("partsAnsItem", partsAnsItem);
          log.debug(`--------------------------------------------------------------------`);

          // partsListReqから該当する依頼データを取得
          // PHPテンプレートでは$partsList[$i]['PartsRequest']でアクセス
          const reqItem =
            data?.partsListReq?.find((req) => {
              if (pl_prid === 0) {
                // その他部品の場合、依頼データがない可能性がある
                return false;
              } else {
                // 通常の部品の場合、PRIDまたは部品名でマッチング
                return req?.id === firstPartsItem?.PRID || req?.P_PartsName === firstPartsItem?.P_PartsName;
              }
            }) || (firstPartsItem?.PartsRequest ? { ...firstPartsItem.PartsRequest } : null);

          // グループインデックス（PHPテンプレートの$s相当）
          const index = partsIndex;

          log.debug("partsAnsItem", partsAnsItem);
          // 部品回答ID（その他部品の場合はOthParts4CD、通常部品の場合はid）
          const paid = target_key;

          // 他社成約情報を取得
          const { thCol, thColor, showOtherCompanyContract, seiyakuPrice } = getOtherCompanyContractInfo(pl_prid, data);

          // 部品名とカテゴリアイコンを生成
          const { partsName, categoryIcons, pk3flg } = generatePartsNameAndIcons(firstPartsItem, pl_prid, partskind, formProps);

          // 標準価格が設定されているかどうかを判定
          // 標準価格がある場合は価格入力欄の必須マークを非表示にする
          let hasStandardPrice = firstPartsItem?.StanderdPrice != null && firstPartsItem?.StanderdPrice !== "";

          // displayフラグを計算（いずれかの部品に価格があるかどうか）
          // display=0の場合は未回答用の入力行を表示する
          let display = 0;
          for (let itemIndex = 0; itemIndex < partsAnsItem.length; itemIndex++) {
            const partsItem = partsAnsItem[itemIndex];
            // 価格はStanderdPrice、Price、PartsPriceのいずれかから取得
            const price = partsItem?.StanderdPrice || partsItem?.Price || partsItem?.PartsPrice;
            if (price) {
              display = 1;
              break;
            }
          }

          // 行追加ボタンを表示するかどうか（通常部品で追加部品でない場合のみ）
          const is_add_button = pl_prid > 0 && is_add_parts == false;
          // tbodyのID（グループごとに一意）
          const tbodyname = `parts-${index}`;
          log.debug(`is_add_parts:${is_add_parts}  is_add_button:${is_add_button} paid:${paid} target_key:${target_key} pl_prid:${pl_prid} pl_paid:${pl_paid} firstPartsItem:${firstPartsItem}`);
          return (
            <React.Fragment key={`group-${index}`}>
              {/* thead */}
              <thead>
                {/* 部品名ヘッダー行 */}
                <_style.PLHeadTr1>
                  {/* 他社成約表示 */}
                  {showOtherCompanyContract ? (
                    <>
                      <_style.PLHeadTh1
                        className={`title ${trClass}`}
                        scope="row"
                        style={{ borderRightStyle: "none", background: "#696969", textAlign: "center", verticalAlign: "middle" }}
                      >
                        <Image src="/images/tasha.png" alt="他社成約" width={120} height={34} />
                      </_style.PLHeadTh1>

                      {/* 成約金額表示（PHPテンプレート246行目相当） */}
                      {/* {seiyakuPrice !== null && ( */}
                      {/*  <_style.PLHeadTh1 colSpan="2" className={`title ${trClass}`} scope="row" style={{ borderRightStyle: "none", borderLeftStyle: "none", background: "#696969" }}> */}
                      {/* <p>成約金額：{formProps.formatNumber(seiyakuPrice)}円</p> */}
                      {/* </_style.PLHeadTh1> */}
                      {/* )} */}
                    </>
                  ) : null}

                  <_style.PLHeadTh1 colSpan={thCol} className={`title ${trClass}`} scope="row" style={thColor}>
                    {partsName}
                    {/* 部品カテゴリアイコン（PKID3は除く） */}
                    {categoryIcons}
                    {/* PKID3（外装）を最後に表示 */}
                    {pk3flg === 1 && (
                      <>
                        　　<span className="icon_g">{formProps.getCategoryName(3, partskind)}</span>
                      </>
                    )}
                    {/* 部品備考 */}
                    {firstPartsItem?.Bikou && <p style={{ fontWeight: "normal" }}>({firstPartsItem.Bikou})</p>}
                  </_style.PLHeadTh1>
                </_style.PLHeadTr1>

                {/* ヘッダー行 */}
                <_style.PLHeadTr2>
                  <_style.PLHeadTh2>&nbsp;</_style.PLHeadTh2>
                  <_style.PLHeadTh2 scope="row">純正品番</_style.PLHeadTh2>
                  <_style.PLHeadTh2>数量</_style.PLHeadTh2>
                  <_style.PLHeadTh2>
                    <div>部品カテゴリ</div>
                    <_style.RequiredMark src="/images/must.png" alt="必須" />
                  </_style.PLHeadTh2>
                  <_style.PLHeadTh2>
                    <div>価格(税別)</div>
                    {!hasStandardPrice && <_style.RequiredMark src="/images/must.png" alt="必須" />}
                  </_style.PLHeadTh2>
                  {isAfterCarryTaxinDate ? (
                    <_style.PLHeadTh2>
                      <div>送料(税別)</div>
                      {!hasStandardPrice && <_style.RequiredMark src="/images/must.png" alt="必須" />}
                    </_style.PLHeadTh2>
                  ) : (
                    <_style.PLHeadTh2>
                      <div>
                        送料<span style={{ fontWeight: "bold", backgroundColor: "rgb(255, 255, 0)" }}>(税込)</span>
                      </div>
                      <_style.RequiredMark src="/images/must.png" alt="必須" />
                    </_style.PLHeadTh2>
                  )}
                  <_style.PLHeadTh2>
                    <div>発送可能日</div>
                    <_style.RequiredMark src="/images/must.png" alt="必須" />
                  </_style.PLHeadTh2>
                  <_style.PLHeadTh2>
                    <div>見積有効期限</div>
                    <_style.RequiredMark src="/images/must.png" alt="必須" />
                  </_style.PLHeadTh2>
                  <_style.PLHeadTh2>カラー番号</_style.PLHeadTh2>
                  <_style.PLHeadTh2>カラー名称</_style.PLHeadTh2>
                  <_style.PLHeadTh2>写真</_style.PLHeadTh2>
                  <_style.PLHeadTh2>部品備考</_style.PLHeadTh2>
                  <_style.PLHeadTh2 scope="row">自社管理番号</_style.PLHeadTh2>
                </_style.PLHeadTr2>
              </thead>

              {/* tbody */}
              <tbody id={tbodyname}>
                {/* 回答行を表示 */}
                {(() => {
                  // グループ内の部品をループ（items配列を使用）
                  // PHPテンプレート: for (; $i < count($partsList); $i++):
                  return partsAnsItem.map((partsItem, itemIndex) => {
                    // 追加行かどうかを判定
                    const is_add_row = utils.isNull(partsItem.is_add_row) ? false : partsItem.is_add_row;

                    hasStandardPrice = partsItem?.StanderdPrice != null && partsItem?.StanderdPrice !== "";

                    // 在庫確認ボタンの表示判定
                    // PHPテンプレート: getQabButtonNameはAqbDataを第3引数として受け取る
                    const dataLimit = formProps.getLaterLimit(data?.request?.Request_Limit, partsItem?.Sell_Limit);
                    const btNameQab = formProps.getQabButtonNameWithAqb(partsItem?.id || "", dataLimit);

                    // Hacchu_flg（発注フラグ）による状態判定
                    // 発注フラグに応じて状態テキスト、背景色、読み取り専用フラグを取得
                    const hacchuFlg = partsItem?.Hacchu_flg;
                    const status = formProps.getHacchuStatus(hacchuFlg);
                    const { jyotai, bgcolor, view_flg } = status;

                    // 読み取り専用モードかどうか（view_flg !== 0の場合は読み取り専用）
                    const is_readOnly = view_flg !== 0;
                    log.debug(
                      `partsName:${partsName} is_readOnly:${is_readOnly} view_flg:${view_flg} paid:${paid} itemIndex:${itemIndex} hasStandardPrice:${hasStandardPrice} partsItem?.StanderdPrice:${partsItem?.StanderdPrice}`
                    );
                    log.debug(`itemIndex:${itemIndex} partsItem`, partsItem);
                    log.debug(`btNameQab:${btNameQab} jyotai:${jyotai} bgcolor:${bgcolor} view_flg:${view_flg}`);

                    // 部品カテゴリ名を取得
                    const partsKindName = formProps.getCategoryName(partsItem?.PartsKindID, partskind);
                    // シーケンス番号（デフォルトは1）
                    const seqnum = partsItem?.Seqnum || 1;

                    // 分類コード（BunruiCD）が1の場合はカラーフィールドを表示
                    const bunruiCD = partsItem?.BunruiCD;
                    const showColorFields = Number(bunruiCD) === 1;
                    const seqnumValue = partsItem?.Seqnum || itemIndex + 1;

                    return (
                      <tr key={`input-${paid}-${itemIndex}`}>
                        <td>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                          {jyotai}
                          {jyotai && <br />}
                          {btNameQab && (
                            <Image
                              src={`/images/${btNameQab}.png`}
                              className="qabOpen btn_gray"
                              data-key={`${answer?.id}/${paid}`}
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                e.preventDefault();
                                qaabDialogProps.open({
                                  mitsumoriNo: quote_number,
                                  raid: partsItem?.RAID || null,
                                  paid: partsItem?.id || null,
                                  isQuestion: state.user?.Gyoshu_flg === 2,
                                });
                              }}
                              alt="在庫確認"
                              width={90}
                              height={24}
                            />
                          )}
                          </div>
                        </td>
                        <td scope="row">
                          {is_readOnly
                            ? partsItem?.PartsJyunseiCode || ""
                            : formProps.input_PartsJyunseiCode({
                                label: "純正品番",
                                value: partsItem?.PartsJyunseiCode || "",
                                style: INPUT_STYLE,
                                onChange: (e, params) => {
                                  addPartsData(paid, itemIndex, "PartsJyunseiCode", e.target.value);
                                },
                              })}
                        </td>
                        <td style={{ width: "2em" }}>{partsItem?.Suryo || 1}</td>
                        <td style={INPUT_STYLE}>
                          {/* 部品カテゴリ*/}
                          {is_readOnly
                            ? partsItem?.PartsKindName || ""
                            : formProps.select_PartsKindID({
                                value: partsItem?.PartsKindID || "",
                                style: INPUT_STYLE,
                                onChange: (e, params) => {
                                  addPartsData(paid, itemIndex, "PartsKindID", e.target.value);
                                },
                              })}
                        </td>
                        <td style={INPUT_STYLE}>
                          {/* 部品価格*/}
                          {(is_readOnly || !is_add_row) && hasStandardPrice
                            ? formProps.formatNumber(partsItem?.StanderdPrice || "0") + "円"
                            : formProps.input_StanderdPrice({
                                value: partsItem?.StanderdPrice || "",
                                style: INPUT_STYLE,
                                onChange: (e, params) => {
                                  addPartsData(paid, itemIndex, "StanderdPrice", e.target.value);
                                },
                              })}
                        </td>
                        <td style={INPUT_STYLE}>
                          {/* 送料*/}
                          {(is_readOnly || !is_add_row) && hasStandardPrice
                            ? (() => {
                                const soryoValue = partsItem?.Soryo || 0;
                                return soryoValue === 0 ? "送料込" : `${formProps.formatNumber(soryoValue)}円`;
                              })()
                            : formProps.input_Soryo({
                                value: partsItem?.Soryo || "",
                                style: INPUT_STYLE,
                                onChange: (e, params) => {
                                  addPartsData(paid, itemIndex, "Soryo", e.target.value);
                                },
                              })}
                        </td>
                        <td>
                          {/* 配送方法*/}
                          {is_readOnly
                            ? formProps.getHassoStatusText(partsItem?.Hasso)
                            : formProps.select_Hasso({
                                value: partsItem?.Hasso || "",
                                style: INPUT_STYLE,
                                onChange: (e, params) => {
                                  addPartsData(paid, itemIndex, "Hasso", e.target.value);
                                },
                              })}
                        </td>
                        <td>
                          {/* 納期*/}
                          {is_readOnly
                            ? formProps.formatDate(partsItem?.Sell_Limit || "") || ""
                            : formProps.datePicker_Sell_Limit({
                                value: dayjs(partsItem?.Sell_Limit || "").format("YYYY-MM-DD") || "",
                                style: INPUT_STYLE,
                                onChange: (value, params) => {
                                  addPartsData(paid, itemIndex, "Sell_Limit", value);
                                },
                              })}
                        </td>
                        <td>
                          {/* カラー番号*/}
                          {is_readOnly
                            ? partsItem?.ColorNo || ""
                            : showColorFields
                              ? formProps.input_ColorNo({
                                  value: partsItem?.ColorNo || "",
                                  style: INPUT_STYLE,
                                  onChange: (e, params) => {
                                    addPartsData(paid, itemIndex, "ColorNo", e.target.value);
                                  },
                                })
                              : null}
                        </td>
                        <td>
                          {/* カラー名称*/}
                          {is_readOnly
                            ? partsItem?.ColorName || ""
                            : showColorFields
                              ? formProps.input_ColorName({
                                  value: partsItem?.ColorName || "",
                                  style: INPUT_STYLE,
                                  onChange: (e, params) => {
                                    addPartsData(paid, itemIndex, "ColorName", e.target.value);
                                  },
                                })
                              : null}
                        </td>
                        <td>
                          {/* 写真アイコン*/}
                          {renderPhotoIcons({
                            is_readOnly,
                            pl_prid,
                            paid,
                            partsItem,
                            seqnum: seqnumValue,
                            data,
                            resPict,
                            quote_number,
                            photoDialogProps,
                            fileupDialogProps,
                            formProps,
                          })}
                        </td>
                        <td>
                          {/* 保証選択*/}
                          {is_readOnly ? (
                            formProps.nl2br(partsItem?.Bikou || "")
                          ) : (
                            <>
                              {/* 保証選択 */}
                              {/* 保証選択 @TODO 要チェック */}
                              {data?.hoshoSelect && Object.keys(data.hoshoSelect).length > 0 && (
                                <div style={{ width: "100%", textAlign: "left" }}>
                                  {formProps.select(`hselect_${itemIndex}`, {
                                    value: String(partsItem?.HoshoSelect) || "0",
                                    options: formProps.generateHoshoSelectOptions(data.hoshoSelect),
                                    style: { width: "100%", float: "left" },
                                  })}

                                  <_style.GrayBtn
                                    style={{ marginLeft: "12px", marginTop: "4px" }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      const _bikou = partsItem?.Bikou || "";
                                      const _hoshoSelect = form[`hselect_${itemIndex}`];
                                      let _value = _bikou + _hoshoSelect;
                                      addPartsData(paid, itemIndex, "Bikou", _value);
                                    }}
                                  >
                                    追加
                                  </_style.GrayBtn>
                                </div>
                              )}
                              {/* 部品備考textarea */}
                              {formProps.input_Bikou({
                                value: partsItem?.Bikou || "",
                                style: { width: "100%" },
                                onChange: (e, params) => {
                                  addPartsData(paid, itemIndex, "Bikou", e.target.value);
                                },
                              })}
                            </>
                          )}
                        </td>
                        <td scope="row" style={{ backgroundColor: "#FDF5E6" }}>
                          {/* 自社管理番号 */}
                          {is_readOnly
                            ? partsItem?.JishaNo || ""
                            : formProps.input_JishaNo({
                                value: partsItem?.JishaNo || "",
                                style: { width: "10em" },
                                onChange: (e, params) => {
                                  addPartsData(paid, itemIndex, "JishaNo", e.target.value);
                                },
                              })}
                          <br />
                          {/* PrepKindラジオボタン */}
                          {is_readOnly
                            ? formProps.getPrepKindText(partsItem?.PrepKind) || ""
                            : formProps.radio_PrepKind({
                                value: partsItem?.PrepKind || "",
                                name: `prepKind-${paid}-${itemIndex}`,
                                style: { cursor: "pointer" },
                                onChange: (e, params) => {
                                  addPartsData(paid, itemIndex, "PrepKind", e.target.value);
                                },
                              })}
                        </td>
                      </tr>
                    );
                  });
                })()}

                {/* 未回答時の行追加（PHPテンプレート635-751行目相当） */}
                {/* display == 0（価格が未設定）かつpl_prid > 0（通常部品）かつis_add_parts == false（追加部品でない）の場合に未回答用の入力行を追加 */}
                {/*display === 0 &&
                  pl_prid > 0 &&
                  !is_add_parts &&
                  (() => {
                    // partsDataから取得、なければ初期値を使用
                    const partsDataItem = partsData[paid]?.[0] || INIT_PARTS_DATA;

                    // 在庫確認ボタンのテキストを取得
                    const aqbData = data?.AqbData || {};
                    let btNameQab = "";
                    if (aqbData[paid]) {
                      // aqbData[paid][2]が未設定の場合は「回答する」、設定済みの場合は「内容を見る」
                      if (!aqbData[paid][2]) {
                        btNameQab = "回答する";
                      } else {
                        btNameQab = "内容を見る";
                      }
                    }

                    // 分類コード（BunruiCD）を取得（firstPartsItemまたはreqItemから）
                    // PHPテンプレート: $partsList[$k]['CBunruiCode']['BunruiCD']
                    const bunruiCD = firstPartsItem?.BunruiCD || reqItem?.CBunruiCode?.BunruiCD;
                    // 分類コードが1の場合はカラーフィールドを表示
                    const showColorFields = Number(bunruiCD) === 1;

                    return (
                      <tr key={`unanswered-${index}`}>
                        <td>
                          {btNameQab && (
                            <a
                              className="qabOpen btn_gray"
                              data-key={`${answer?.id}/${paid}`}
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                e.preventDefault();
                                // TODO: onOpenQabDialogの実装
                              }}
                            >
                              {btNameQab}
                            </a>
                          )}
                        </td>
                        <td scope="row">
                          {formProps.input_PartsJyunseiCode({
                            label: "純正品番",
                            value: partsDataItem?.PartsJyunseiCode || "",
                            style: INPUT_STYLE,
                            onChange: (e, params) => {
                              addPartsData(paid, 0, "PartsJyunseiCode", e.target.value);
                            },
                          })}
                        </td>
                        <td style={{ width: "2em" }}>1</td>
                        <td style={INPUT_STYLE}>
                          {formProps.select_PartsKindID({
                            value: partsDataItem?.PartsKindID || -1,
                            style: INPUT_STYLE,
                            onChange: (e, params) => {
                              addPartsData(paid, 0, "PartsKindID", e.target.value);
                            },
                          })}
                        </td>
                        <td style={INPUT_STYLE}>
                          {formProps.input_StanderdPrice({
                            value: partsDataItem?.StanderdPrice || "",
                            style: INPUT_STYLE,
                            onChange: (e, params) => {
                              addPartsData(paid, 0, "StanderdPrice", e.target.value);
                            },
                          })}
                          円
                        </td>
                        <td style={INPUT_STYLE}>
                          {formProps.input_Soryo({
                            value: partsDataItem?.Soryo || "",
                            style: INPUT_STYLE,
                            onChange: (e, params) => {
                              addPartsData(paid, 0, "Soryo", e.target.value);
                            },
                          })}
                          円
                        </td>
                        <td>
                          {formProps.select_Hasso({
                            value: partsDataItem?.Hasso || "",
                            style: INPUT_STYLE,
                            onChange: (e, params) => {
                              addPartsData(paid, 0, "Hasso", e.target.value);
                            },
                          })}
                        </td>
                        <td>
                          {formProps.datePicker_Sell_Limit({
                            value: partsDataItem?.Sell_Limit || "",
                            style: INPUT_STYLE,
                            onChange: (value, params) => {
                              addPartsData(paid, 0, "Sell_Limit", value);
                            },
                          })}
                        </td>
                        <td>
                          {showColorFields &&
                            formProps.input_ColorNo({
                              value: partsDataItem?.ColorNo || "",
                              style: INPUT_STYLE,
                              onChange: (e, params) => {
                                addPartsData(paid, 0, "ColorNo", e.target.value);
                              },
                            })}
                        </td>
                        <td>
                          {showColorFields &&
                            formProps.input_ColorName({
                              value: partsDataItem?.ColorName || "",
                              style: INPUT_STYLE,
                              onChange: (e, params) => {
                                addPartsData(paid, 0, "ColorName", e.target.value);
                              },
                            })}
                        </td>
                        <td>
                          {renderPhotoIcons({
                            is_readOnly: false,
                            pl_prid,
                            paid: reqItem?.id,
                            partsItem: { id: reqItem?.id },
                            seqnum: 1,
                            data,
                            resPict,
                            quote_number,
                            photoDialogProps,
                            fileupDialogProps,
                            formProps,
                          })}
                        </td>
                        <td>
                          {Object.keys(hoshoSelect).length > 0 && (
                            <>
                              {formProps.select("HoshoSelect", {
                                value: partsDataItem?.HoshoSelect || "",
                                options: generateHoshoSelectOptions(hoshoSelect),
                                style: INPUT_STYLE,
                                onChange: (e, params) => {
                                  addPartsData(paid, 0, "HoshoSelect", e.target.value);
                                },
                              })}
                              <input type="button" className="AddBikou" value="追加" onClick={() => handleAddBikou(index, 0)} />
                              <br />
                            </>
                          )}
                          {formProps.input_Bikou({
                            value: partsDataItem?.Bikou || "",
                            style: { width: "100%" },
                            onChange: (e, params) => {
                              addPartsData(paid, 0, "Bikou", e.target.value);
                            },
                          })}
                        </td>
                        <td scope="row" style={{ backgroundColor: "#FDF5E6" }}>
                          {formProps.input_JishaNo({
                            value: partsDataItem?.JishaNo || "",
                            style: INPUT_STYLE,
                            onChange: (e, params) => {
                              addPartsData(paid, 0, "JishaNo", e.target.value);
                            },
                          })}
                          <br />
                          {formProps.radio_PrepKind({
                            value: partsDataItem?.PrepKind || "",
                            name: `prepKind-${paid}-0`,
                            style: { cursor: "pointer" },
                            onChange: (e, params) => {
                              addPartsData(paid, 0, "PrepKind", e.target.value);
                            },
                          })}
                        </td>
                      </tr>
                    );
                  })()*/}

                {/* 行追加ボタン（pl_prid > 0の場合のみ） */}
                {pl_prid > 0 && !is_add_parts && (
                  <tr>
                    <td colSpan="13" scope="row" style={{ textAlign: "right" }}>
                      <_style.GrayBtn
                        onClick={(e) => {
                          e.preventDefault();
                          setPartsData((prev) => {
                            const currentRow = prev[paid] || [];
                            const updatedRow = [...currentRow];
                            // 既存の最大Seqnumを取得
                            let maxSeqnum = 0;
                            currentRow.forEach((item) => {
                              if (item.Seqnum) maxSeqnum = Math.max(maxSeqnum, item.Seqnum);
                            });
                            updatedRow.push({ ...partsItem, PRID: pl_prid,StanderdPrice: "", Soryo: "", Hasso: "", Bikou: "", is_add_row: true, is_add_parts: false, Seqnum: maxSeqnum + 1 });
                            return {
                              ...prev,
                              [paid]: updatedRow,
                            };
                          });
                        }}
                      >
                        行を追加
                      </_style.GrayBtn>
                    </td>
                  </tr>
                )}
              </tbody>
            </React.Fragment>
          );
        })}
      </_style.PLTable>
    </>
  );
}
